import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Leaf, Plant, Bug, Warning, Gear, Drop, Scissors, Share, Download, CheckCircle, Circle } from '@phosphor-icons/react';
import { DepartmentNav } from '../components/DepartmentNav';
import { PageWhy } from '../components/PageWhy';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const CATEGORY_ICONS = {
  mowing: '✂️',
  fertilizing: '🌱',
  weed_control: '🌿',
  pest_control: '🐛',
  disease_control: '⚠️',
  aeration: '🔧',
  overseeding: '🌾',
  watering: '💧',
  equipment: '⚙️',
  equipment_maintenance: '⚙️',
  planning: '📋',
  general: '📌',
  soil: '🪴',
  pgr: '🧪'
};

const CATEGORY_COLORS = {
  mowing: 'bg-green-100 text-green-800 border-green-200',
  fertilizing: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  weed_control: 'bg-orange-100 text-orange-800 border-orange-200',
  pest_control: 'bg-red-100 text-red-800 border-red-200',
  disease_control: 'bg-purple-100 text-purple-800 border-purple-200',
  aeration: 'bg-amber-100 text-amber-800 border-amber-200',
  overseeding: 'bg-lime-100 text-lime-800 border-lime-200',
  watering: 'bg-blue-100 text-blue-800 border-blue-200',
  equipment: 'bg-gray-100 text-gray-800 border-gray-200',
  equipment_maintenance: 'bg-gray-100 text-gray-800 border-gray-200',
  planning: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  general: 'bg-slate-100 text-slate-800 border-slate-200',
  soil: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pgr: 'bg-teal-100 text-teal-800 border-teal-200'
};

const PRIORITY_STYLES = {
  required: 'border-l-4 border-l-red-500',
  recommended: 'border-l-4 border-l-yellow-500',
  optional: 'border-l-4 border-l-gray-300'
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const SeasonalCalendarPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);
  const [calendar, setCalendar] = useState([]);
  const [calendarSources, setCalendarSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grassType, setGrassType] = useState(user?.grass_type || 'cool-season');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year'
  const [completedTasks, setCompletedTasks] = useState(() => {
    const saved = localStorage.getItem('turfpulse_completed_tasks');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    fetchCalendar();
  }, [grassType]);

  useEffect(() => {
    localStorage.setItem('turfpulse_completed_tasks', JSON.stringify(completedTasks));
  }, [completedTasks]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const endpoint = user
        ? `${API_URL}/api/seasonal-calendar`
        : `${API_URL}/api/seasonal-calendar/share?grass_type=${grassType}`;
      
      const res = await axios.get(endpoint, { withCredentials: true });
      setCalendar(res.data.calendar);
      setCalendarSources(res.data.sources || []);
      if (res.data.grass_type) {
        setGrassType(res.data.grass_type);
      }
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
      toast.error('Failed to load seasonal calendar');
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskCompletion = (monthNum, taskIndex) => {
    const key = `${monthNum}-${taskIndex}`;
    setCompletedTasks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isTaskCompleted = (monthNum, taskIndex) => {
    return completedTasks[`${monthNum}-${taskIndex}`] || false;
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/calendar?grass_type=${grassType}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `TurfPulse Seasonal Lawn Care Calendar (${grassType === 'cool-season' ? 'Cool-Season' : 'Warm-Season'})`,
          text: 'Check out this month-by-month lawn care guide!',
          url: shareUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Calendar link copied to clipboard!');
  };

  const downloadCalendar = () => {
    // Create a styled HTML version for download (works on iOS/Android/Desktop)
    const grassLabel = grassType === 'cool-season' ? 'Cool-Season' : 'Warm-Season';
    let html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>TurfPulse Lawn Care Calendar — ${grassLabel}</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:720px;margin:0 auto;padding:24px;color:#1a1a1a;background:#fff}
  h1{color:#2d5a27;font-size:22px;border-bottom:3px solid #2d5a27;padding-bottom:8px}
  h2{color:#2d5a27;font-size:17px;margin-top:28px;margin-bottom:4px}
  .meta{color:#666;font-size:13px;margin-bottom:24px}
  .task{margin:8px 0;padding:8px 12px;border-left:3px solid #ccc;background:#f9f9f9;border-radius:0 6px 6px 0}
  .task.required{border-left-color:#dc2626;background:#fef2f2}
  .task.recommended{border-left-color:#f59e0b;background:#fffbeb}
  .task-name{font-weight:600;font-size:14px}
  .task-desc{color:#555;font-size:13px;margin-top:2px}
  .legend{display:flex;gap:16px;margin:12px 0;font-size:12px;color:#666}
  .legend span{display:flex;align-items:center;gap:4px}
  .dot{width:10px;height:10px;border-radius:2px;display:inline-block}
  .footer{margin-top:32px;padding-top:12px;border-top:1px solid #e5e5e5;font-size:11px;color:#999;text-align:center}
</style></head><body>
<h1>TurfPulse Lawn Care Calendar</h1>
<div class="meta">Grass Type: ${grassLabel} &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString()}</div>
<div class="legend">
  <span><span class="dot" style="background:#dc2626"></span> Required</span>
  <span><span class="dot" style="background:#f59e0b"></span> Recommended</span>
  <span><span class="dot" style="background:#ccc"></span> Optional</span>
</div>`;

    calendar.forEach(month => {
      html += `<h2>${month.month_name}</h2>`;
      month.tasks.forEach(task => {
        const cls = task.priority === 'required' ? 'required' : task.priority === 'recommended' ? 'recommended' : '';
        html += `<div class="task ${cls}"><div class="task-name">${task.task}</div><div class="task-desc">${task.description}</div></div>`;
      });
    });

    html += `<div class="footer">Generated by TurfPulse — getturfpulse.com<br>Science-based lawn care powered by Growing Degree Days</div></body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TurfPulse-Calendar-${grassLabel.replace(' ', '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Calendar downloaded!');
  };

  const getCurrentMonthData = () => {
    return calendar.find(m => m.month === currentMonth) || null;
  };

  const getTaskCounts = () => {
    let total = 0;
    let completed = 0;
    calendar.forEach(month => {
      month.tasks.forEach((_, idx) => {
        total++;
        if (isTaskCompleted(month.month, idx)) completed++;
      });
    });
    return { total, completed };
  };

  const { total: totalTasks, completed: completedCount } = getTaskCounts();

  return (
    <div data-testid="seasonal-calendar-page" className="min-h-screen bg-background">
      <SEO
        title="Seasonal Lawn Care Calendar"
        description="Month-by-month lawn care calendar for warm and cool-season grasses. Know what to do every month based on GDD science. Fertilizing, mowing, aerating, and treatment schedules."
        keywords="lawn care calendar, seasonal lawn care schedule, monthly lawn tasks, bermuda grass calendar, when to fertilize lawn, when to aerate, lawn care by month"
        path="/calendar"
        imageAlt="TurfPulse Seasonal Calendar — Month-by-month lawn care task guide"
      />
      <DepartmentNav />

      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Calendar weight="duotone" size={36} className="text-primary" />
              Seasonal Lawn Care Calendar
            </h1>
            <p className="text-muted-foreground mt-1">
              Month-by-month guide for a healthy, beautiful lawn
            </p>
            <PageWhy testId="seasonal-calendar-page-why">See the whole year at a glance — so you're never caught off-guard by the next task your lawn needs.</PageWhy>
          </div>

          <div className="flex items-center gap-3">
            <Select value={grassType} onValueChange={setGrassType}>
              <SelectTrigger data-testid="grass-type-select" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cool-season">🌿 Cool-Season Grass</SelectItem>
                <SelectItem value="warm-season">☀️ Warm-Season Grass</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={handleShare} data-testid="share-calendar-btn">
              <Share weight="bold" size={18} className="mr-2" />
              Share
            </Button>
            <Button variant="outline" onClick={downloadCalendar}>
              <Download weight="bold" size={18} className="mr-2" />
              Download
            </Button>
          </div>
        </div>

        {/* Progress Card */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Annual Progress</p>
                  <p className="text-2xl font-bold">{completedCount} / {totalTasks} tasks</p>
                </div>
                <div className="w-48 h-3 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-500 rounded"></span> Required
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-yellow-500 rounded"></span> Recommended
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-gray-300 rounded"></span> Optional
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* View Toggle */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="mb-6">
          <TabsList>
            <TabsTrigger value="month">Month View</TabsTrigger>
            <TabsTrigger value="year">Year Overview</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : viewMode === 'month' ? (
          <>
            {/* Month Selector */}
            <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
              {MONTHS.map((name, idx) => {
                const monthNum = idx + 1;
                const monthData = calendar.find(m => m.month === monthNum);
                const monthTasks = monthData?.tasks || [];
                const completedInMonth = monthTasks.filter((_, i) => isTaskCompleted(monthNum, i)).length;
                const isCurrentMonth = monthNum === new Date().getMonth() + 1;
                
                return (
                  <Button
                    key={name}
                    variant={currentMonth === monthNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentMonth(monthNum)}
                    className={`flex-shrink-0 ${isCurrentMonth && currentMonth !== monthNum ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                    data-testid={`month-btn-${monthNum}`}
                  >
                    {name.slice(0, 3)}
                    {monthTasks.length > 0 && (
                      <span className="ml-1.5 text-xs opacity-70">
                        {completedInMonth}/{monthTasks.length}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>

            {/* Current Month Tasks */}
            {(() => {
              const monthData = getCurrentMonthData();
              if (!monthData) {
                return (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground">No data for this month</p>
                    </CardContent>
                  </Card>
                );
              }
              
              return (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl">{monthData.month_name}</CardTitle>
                    <CardDescription>
                      {monthData.tasks.length} tasks for this month
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {monthData.tasks.map((task, idx) => {
                        const isCompleted = isTaskCompleted(monthData.month, idx);
                        
                        return (
                          <div
                            key={idx}
                            data-testid={`task-${monthData.month}-${idx}`}
                            className={`p-4 rounded-lg border ${PRIORITY_STYLES[task.priority]} ${
                              isCompleted ? 'bg-secondary/50 opacity-70' : 'bg-card'
                            } transition-all`}
                          >
                            <div className="flex items-start gap-4">
                              <button
                                onClick={() => toggleTaskCompletion(monthData.month, idx)}
                                className="mt-1 flex-shrink-0"
                                data-testid={`toggle-task-${monthData.month}-${idx}`}
                              >
                                {isCompleted ? (
                                  <CheckCircle weight="fill" size={24} className="text-primary" />
                                ) : (
                                  <Circle size={24} className="text-muted-foreground" />
                                )}
                              </button>
                              
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xl">{CATEGORY_ICONS[task.category] || '📌'}</span>
                                  <h3 className={`font-semibold ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                                    {task.task}
                                  </h3>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_COLORS[task.category] || 'bg-gray-100'}`}>
                                    {task.category.replace('_', ' ')}
                                  </span>
                                </div>
                                <p className={`text-sm ${isCompleted ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                                  {task.description}
                                </p>
                                {task.gdd_trigger && (
                                  <p className="text-xs text-primary mt-2 font-medium">
                                    GDD Trigger: {task.gdd_trigger} GDD
                                  </p>
                                )}
                              </div>
                              
                              <span className={`text-xs px-2 py-1 rounded capitalize ${
                                task.priority === 'required' ? 'bg-red-100 text-red-700' :
                                task.priority === 'recommended' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {task.priority}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </>
        ) : (
          /* Year Overview */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {calendar.map((month) => {
              const completedInMonth = month.tasks.filter((_, i) => isTaskCompleted(month.month, i)).length;
              const isCurrentMonth = month.month === new Date().getMonth() + 1;
              
              return (
                <Card
                  key={month.month}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    isCurrentMonth ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => {
                    setCurrentMonth(month.month);
                    setViewMode('month');
                  }}
                  data-testid={`year-month-card-${month.month}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{month.month_name}</CardTitle>
                      <span className="text-sm text-muted-foreground">
                        {completedInMonth}/{month.tasks.length}
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${month.tasks.length > 0 ? (completedInMonth / month.tasks.length) * 100 : 0}%` }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-1">
                      {month.tasks.slice(0, 3).map((task, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className={`w-2 h-2 rounded-full ${
                            task.priority === 'required' ? 'bg-red-500' :
                            task.priority === 'recommended' ? 'bg-yellow-500' : 'bg-gray-300'
                          }`} />
                          <span className={`truncate ${isTaskCompleted(month.month, idx) ? 'line-through text-muted-foreground' : ''}`}>
                            {task.task}
                          </span>
                        </div>
                      ))}
                      {month.tasks.length > 3 && (
                        <p className="text-xs text-muted-foreground">+{month.tasks.length - 3} more tasks</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Source Attribution */}
        {calendarSources.length > 0 && (
          <div className="mt-8 pt-4 border-t border-border/50" data-testid="calendar-sources">
            <p className="text-[11px] text-muted-foreground/60 italic">
              Calendar recommendations adapted from: {calendarSources.join(' · ')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
